import { useEffect, useState } from 'react';
import { Input } from '@monorepo/base/src/components/form/input/input';
import { SecondaryButton } from '@monorepo/base/src/components/buttons/buttons';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import { observer } from 'mobx-react';
import { CampaignDestinationLinkModel } from '../../../../models/campaign.model';
import {
	applyDesiredPercentagesToWeights,
	areDesiredPercentagesValid,
	formatTrafficPercentage,
	getDesiredPercentageDefaults,
	getDestinationAllocationKey,
	sumActiveWeights,
	sumDesiredPercentages,
} from '../../../../utils/campaign-destination-weights';
import styles from './destinations-weight-table.module.scss';

type Props = {
	links: CampaignDestinationLinkModel[];
	editable?: boolean;
	showPausedDestinations?: boolean;
	enableWeightAllocation?: boolean;
};

const EditableNumberCell = observer(
	({
		value,
		onCommit,
		debugId,
		nullable,
		inforcedMinimumValue,
	}: {
		value: number | null | undefined;
		onCommit: (next: number | null) => void;
		debugId: string;
		nullable?: boolean;
		inforcedMinimumValue?: number;
	}) => {
		const displayValue = value == null ? '' : String(value);
		if (inforcedMinimumValue && value && value < inforcedMinimumValue) {
			value = inforcedMinimumValue;
		}

		return (
			<Input
				className={styles.fieldInput}
				value={displayValue}
				onValue={next => {
					if (next === '') {
						if (nullable) {
							onCommit(null);
						} else {
							onCommit(inforcedMinimumValue ? inforcedMinimumValue : 1);
						}
						return;
					}
					if (!Number.isNaN(Number(next)) && Number(next) >= (inforcedMinimumValue ?? 1)) {
						onCommit(Number(next));
					}
				}}
				debugProps={{ dataAttrs: [new DataAttribute('id', debugId)] }}
			/>
		);
	}
);

export const DestinationsWeightTable = observer(
	({ links, editable = false, showPausedDestinations = false, enableWeightAllocation = false }: Props) => {
		const showAllocation = enableWeightAllocation || editable;
		const totalActiveWeight = sumActiveWeights(links);
		const allocationSeed = links
			.map(link => `${getDestinationAllocationKey(link)}:${link.isActive() ? 1 : 0}:${link.weight ?? 0}`)
			.join('|');

		const [desiredPercentages, setDesiredPercentages] = useState<Record<string, string>>(() => getDesiredPercentageDefaults(links));
		const [isWeightAllocationApplied, setIsWeightAllocationApplied] = useState(false);

		useEffect(() => {
			if (!showAllocation) {
				return;
			}
			setDesiredPercentages(getDesiredPercentageDefaults(links));
		}, [showAllocation, allocationSeed]);

		const desiredPercentagesSum = sumDesiredPercentages(links, desiredPercentages);
		const isDesiredPercentagesSumValid = areDesiredPercentagesValid(desiredPercentagesSum);
		const visibleLinks = links.filter(link => (showPausedDestinations ? true : link.isActive()));

		const onApplyDesiredPercentages = () => {
			if (!isDesiredPercentagesSumValid) {
				return;
			}
			applyDesiredPercentagesToWeights(links, desiredPercentages);
			setDesiredPercentages(getDesiredPercentageDefaults(links));
			setIsWeightAllocationApplied(true);
			setTimeout(() => {
				setIsWeightAllocationApplied(false);
			}, 1000);
		};

		return (
			<div className={styles.tableWrapper}>
				<table className={styles.destinationsWeightTable}>
					<thead>
						<tr>
							<th>Destination</th>
							<th>Weight</th>
							<th>%</th>
							{showAllocation ? <th>Desired %</th> : null}
							<th>Max Daily Sales</th>
							<th>Max Daily Loss</th>
							<th>Status</th>
						</tr>
					</thead>
					<tbody>
						{visibleLinks.map(link => {
							const paused = !link.isActive();
							const catalogId = link.getCatalogDestinationId();
							const allocationKey = getDestinationAllocationKey(link);
							const textClass = paused ? styles.pausedDestinationText : undefined;
							return (
								<tr key={String(catalogId ?? link.id)} className={paused ? styles.pausedDestinationRow : undefined}>
									<td className={`${styles.destinationName}${textClass ? ` ${textClass}` : ''}`} title={link.getName()}>
										{link.getName()}
									</td>
									<td className={textClass}>
										{editable ? (
											<EditableNumberCell
												value={link.weight ?? 1}
												onCommit={next => link.setWeight(next ?? 1)}
												debugId={`inline_weight_${catalogId}`}
												inforcedMinimumValue={1}
											/>
										) : (
											(link.weight ?? 1)
										)}
									</td>
									<td className={textClass}>{formatTrafficPercentage(link, links)}</td>
									{showAllocation ? (
										<td className={textClass}>
											{paused ? (
												'—'
											) : (
												<Input
													className={styles.desiredPercentInput}
													value={desiredPercentages[allocationKey] ?? ''}
													onValue={value => {
														if (value === '' || (/^\d+$/.test(value) && value !== '0')) {
															setDesiredPercentages(prev => ({
																...prev,
																[allocationKey]: value,
															}));
														}
													}}
													debugProps={{
														dataAttrs: [new DataAttribute('id', `desired_weight_percentage_${catalogId}`)],
													}}
												/>
											)}
										</td>
									) : null}
									<td className={textClass}>
										{editable ? (
											<EditableNumberCell
												value={link.daily_max_sales}
												onCommit={next => link.setDailyMaxSales(next)}
												debugId={`inline_daily_max_sales_${catalogId}`}
												nullable
											/>
										) : link.daily_max_sales == null ? (
											'—'
										) : (
											link.daily_max_sales
										)}
									</td>
									<td className={textClass}>
										{editable ? (
											<EditableNumberCell
												value={link.daily_max_loss}
												onCommit={next => link.setDailyMaxLoss(next)}
												debugId={`inline_daily_max_loss_${catalogId}`}
												nullable
											/>
										) : link.daily_max_loss == null ? (
											'—'
										) : (
											link.daily_max_loss
										)}
									</td>
									<td className={textClass}>{paused ? 'Paused' : 'Active'}</td>
								</tr>
							);
						})}
					</tbody>
					<tfoot>
						<tr>
							<td>Total (active)</td>
							<td>{totalActiveWeight}</td>
							<td>{totalActiveWeight > 0 ? '100%' : '—'}</td>
							{showAllocation ? <td /> : null}
							<td />
							<td />
							<td />
						</tr>
					</tfoot>
				</table>

				{showAllocation ? (
					<div className={styles.allocationFooter}>
						<div className={styles.allocationNote}>
							{isDesiredPercentagesSumValid
								? 'Percentages total 100%.'
								: `Percentages must total 100%. Current total: ${desiredPercentagesSum.toFixed(0)}%.`}
						</div>
						<div className={styles.allocationButtonWrapper}>
							<SecondaryButton
								className={`${styles.allocationButton} ${isWeightAllocationApplied ? styles.allocationButtonApplied : ''}`}
								disabled={!isDesiredPercentagesSumValid}
								onClick={onApplyDesiredPercentages}
								debugProps={{ dataAttrs: [new DataAttribute('id', 'apply_destination_weight_percentages')] }}>
								Apply percentages to weights
							</SecondaryButton>
						</div>
					</div>
				) : null}
			</div>
		);
	}
);
